import React from 'react';
import { Box, Button, ContentTemplate, Grid, MUIDataTableOptions, Typography } from 'web-library';
import { useTranslation } from 'react-i18next';
import { Theme as AugmentedTheme } from '@mui/material/styles';

import { makeStyles } from 'tss-react/mui';
import { useHistory } from 'react-router-dom';
import { CandidatePsleResultReleaseSchema } from '../../models/@candidate/CandidateResultReleaseSchema';
import CustomDataTable from '../../components/datatables/CustomDataTable';
import key from '../../routes/key';
import PsleExamDetail from '../../components/PsleExamDetail';
import SimplePsleCandidateDetail from '../../components/SimplePsleCandidateDetail';
import AdditionalResultLinks from './AdditionalResultLinks';
import ResultSlipExplanitory from '../../components/ResultSlipExplanitory';

export interface PsleResultsProps {
  candidateResultData: CandidatePsleResultReleaseSchema;
  eligibleForResit: boolean;
  eligibleForAppeal: boolean;
  listingCode?: any;
  examLevel: string;
}

const useStyles = makeStyles()((theme) =>
({
  psleScoreContainer: {
    paddingTop: theme.spacing(4.5),
    paddingBottom: theme.spacing(4.5),
  },
  resultTable: {
    paddingBottom: theme.spacing(4.5),
  },
  marginBottom: {
    marginBottom: theme.spacing(7),
  },
  explanatoryNotes: {
    marginTop: theme.spacing(4),
  },
}));

/** define datatable options
 * */
const dataTableOptions: MUIDataTableOptions = {
  elevation: 3,
  filter: false,
  search: false,
  confirmFilters: false,
  download: false,
  print: false,
  viewColumns: false,
  selectableRows: 'none',
  responsive: 'standard',
  pagination: false,
};

const { result } = key.child;
const { coordinatorResultList, coordinatorResultRelease } = key.result.child;

const PsleResults = ({
  candidateResultData,
  eligibleForResit,
  eligibleForAppeal,
  listingCode,
  examLevel,
}: PsleResultsProps) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const history = useHistory();

  return (
    <Grid container direction="row">
      <Grid xs={12}>
        <ContentTemplate isContinuable>
          <Box>
            <PsleExamDetail
              examLevel={candidateResultData.examLevel}
              year={candidateResultData.examYear}
              examCentreCity={candidateResultData.examCentreCity}
              examCentreCountry={candidateResultData.examCentreCountry}
            />
          </Box>
        </ContentTemplate>
      </Grid>
      <Grid xs={12}>
        <ContentTemplate continuous isContinuable isDividerTop>
          <SimplePsleCandidateDetail
            name={candidateResultData.candidateName}
            id={candidateResultData.candidateId}
            indexNo={candidateResultData.candidateIndexNum}
            hypyName={candidateResultData.hanyuPinyinName}
            schoolName={candidateResultData.examSchool}
            examCode={candidateResultData.examCode}
          />
        </ContentTemplate>
      </Grid>
      <Grid xs={12}>
        <ContentTemplate type="full" continuous>
          <Box className={classes.marginBottom}>
            <Box className={classes.resultTable}>
              <CustomDataTable
                dataTableType="datatable"
                columns={[
                  {
                    name: 'subjectName',
                    label: t('exam->>ResultDetail.Subject'),
                  },
                  {
                    name: 'grade',
                    label: t('exam->>ResultDetail.AchievementLevel'),
                  },
                ]}
                data={candidateResultData.grades}
                options={dataTableOptions}
                title=""
              />

              {candidateResultData.examCode === 'PSLE' && (
                <Box>
                  <Grid className={classes.psleScoreContainer} container spacing={2}>
                    <Grid md={6} xs={12}>
                      <Typography variant="h5">{t('exam->>ResultDetail.PsleScore')}</Typography>
                    </Grid>
                    <Grid md={6} xs={12}>
                      <Typography variant="body2">{candidateResultData.psleScore}</Typography>
                    </Grid>
                  </Grid>
                  <Grid container spacing={2}>
                    <Grid md={6} xs={12}>
                      <Typography variant="h5">
                        {t('exam->>ResultDetail.EligibleCourses')}
                      </Typography>
                    </Grid>
                    <Grid md={6} xs={12}>
                      <Typography variant="body2">
                        {candidateResultData.courseEligibility}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              )}
              <AdditionalResultLinks
                eligibleForAppeal={eligibleForAppeal}
                eligibleForResit={eligibleForResit}
              />
              <Grid container className={classes.explanatoryNotes}>
                <ResultSlipExplanitory
                  examLevel={examLevel}
                  examCode={candidateResultData.examCode}
                />
              </Grid>
            </Box>
            {listingCode && (
              <Button
                variant="outlined"
                onClick={() =>
                  history.push(
                    `/${result}/${coordinatorResultList.route}/${coordinatorResultRelease.route}`,
                    listingCode
                  )
                }
              >
                Back to List
              </Button>
            )}
          </Box>
        </ContentTemplate>
      </Grid>
    </Grid>
  );
};

export default PsleResults;
